import { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ── OTP digit boxes ───────────────────────────────────────────────────────────
function OTPBoxes({ value, onChange, disabled }) {
  const refs = [useRef(), useRef(), useRef(), useRef()];
  const digits = (value + "    ").slice(0, 4).split("");

  const handleInput = (i, char) => {
    if (!/^\d$/.test(char)) return;
    const next = digits.slice();
    next[i] = char;
    onChange(next.join("").trim());
    if (i < 3) refs[i + 1].current?.focus();
  };

  const handleKeyDown = (i, e) => {
    if (e.key === "Backspace") {
      e.preventDefault();
      const next = digits.slice();
      if (next[i].trim()) {
        next[i] = " ";
        onChange(next.join("").trim());
      } else if (i > 0) {
        next[i - 1] = " ";
        onChange(next.join("").trim());
        refs[i - 1].current?.focus();
      }
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData("text").replace(/\D/g, "").slice(0, 4);
    onChange(pasted);
    const lastIdx = Math.min(pasted.length, 3);
    refs[lastIdx].current?.focus();
  };

  return (
    <div className="flex gap-3" onPaste={handlePaste}>
      {[0, 1, 2, 3].map((i) => (
        <input
          key={i}
          ref={refs[i]}
          data-testid={`otp-digit-${i}`}
          type="text"
          inputMode="numeric"
          maxLength={1}
          value={digits[i].trim()}
          disabled={disabled}
          onChange={(e) => handleInput(i, e.target.value.slice(-1))}
          onKeyDown={(e) => handleKeyDown(i, e)}
          className="w-14 h-14 text-center text-2xl font-bold border-2 border-slate-200 rounded-lg
                     focus:border-[#1656a4] focus:outline-none transition-colors
                     disabled:opacity-50 bg-white text-slate-900"
        />
      ))}
    </div>
  );
}

// ── Main login page ───────────────────────────────────────────────────────────
export default function AdminLogin() {
  const navigate = useNavigate();
  const [step, setStep] = useState("email"); // 'email' | 'otp'
  const [email, setEmail] = useState("");
  const [otp, setOtp] = useState("");
  const [loading, setLoading] = useState(false);
  const [devOtp, setDevOtp] = useState(null);

  useEffect(() => {
    const token = localStorage.getItem("dynamo_token");
    if (token) navigate("/admin/dashboard", { replace: true });
  }, [navigate]);

  const sendOtp = async (e) => {
    e.preventDefault();
    if (!email) return;
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/auth/send-otp`, { email });
      if (data.dev_mode) {
        setDevOtp(data.dev_otp);
        toast.info("Dev mode: OTP shown below (no email configured yet)");
      } else {
        toast.success(`Code sent to ${email}`);
      }
      setStep("otp");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to send code");
    } finally {
      setLoading(false);
    }
  };

  const verifyOtp = async (e) => {
    e.preventDefault();
    if (otp.length < 4) return;
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/auth/verify-otp`, { email, otp });
      localStorage.setItem("dynamo_token", data.token);
      localStorage.setItem("dynamo_email", data.email);
      toast.success("Welcome to Dynamo Gymnastics!");
      navigate("/admin/dashboard", { replace: true });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Verification failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 px-6 py-12">
      <div className="w-full max-w-sm">
          {/* Logo */}
          <div className="mb-8 text-center">
            <h1 className="text-2xl font-bold text-[#1656a4]" style={{ fontFamily: "Outfit, sans-serif" }}>
              Dynamo Gymnastics
            </h1>
            <p className="text-slate-500 text-sm mt-1">Taster Session Portal</p>
          </div>

          <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-8">
            {step === "email" ? (
              <>
                <h2 className="text-xl font-semibold text-slate-900 mb-1" style={{ fontFamily: "Outfit, sans-serif" }}>
                  Staff Login
                </h2>
                <p className="text-slate-500 text-sm mb-6">
                  Enter your @dynamogymnastics.co.uk email to receive a login code.
                </p>
                <form onSubmit={sendOtp} className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1.5">
                      Email address
                    </label>
                    <input
                      data-testid="login-email-input"
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="you@dynamogymnastics.co.uk"
                      required
                      className="w-full px-3 py-2.5 border border-slate-200 rounded-md text-sm
                                 focus:outline-none focus:ring-2 focus:ring-[#1656a4] focus:border-transparent
                                 transition-colors placeholder:text-slate-400"
                    />
                  </div>
                  <button
                    data-testid="send-otp-button"
                    type="submit"
                    disabled={loading}
                    className="w-full bg-[#1656a4] hover:bg-[#124687] text-white font-medium
                               py-2.5 px-4 rounded-md transition-colors disabled:opacity-60
                               text-sm"
                  >
                    {loading ? "Sending…" : "Send Login Code"}
                  </button>
                </form>
              </>
            ) : (
              <>
                <h2 className="text-xl font-semibold text-slate-900 mb-1" style={{ fontFamily: "Outfit, sans-serif" }}>
                  Enter your code
                </h2>
                <p className="text-slate-500 text-sm mb-6">
                  We{devOtp ? "'ve generated" : " sent"} a 4-digit code{" "}
                  {devOtp ? "below" : `to ${email}`}.
                </p>

                {/* Dev mode banner */}
                {devOtp && (
                  <div
                    data-testid="dev-otp-banner"
                    className="mb-5 bg-amber-50 border border-amber-200 rounded-lg px-4 py-3 text-sm"
                  >
                    <p className="font-semibold text-amber-800 mb-0.5">Development Mode</p>
                    <p className="text-amber-700">
                      Your code is:{" "}
                      <span className="font-bold tracking-widest text-lg">{devOtp}</span>
                    </p>
                    <p className="text-amber-600 text-xs mt-1">
                      Add a Resend API key to send real emails.
                    </p>
                  </div>
                )}

                <form onSubmit={verifyOtp} className="space-y-5">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-3">
                      4-digit code
                    </label>
                    <OTPBoxes value={otp} onChange={setOtp} disabled={loading} />
                  </div>
                  <button
                    data-testid="verify-otp-button"
                    type="submit"
                    disabled={loading || otp.length < 4}
                    className="w-full bg-[#1656a4] hover:bg-[#124687] text-white font-medium
                               py-2.5 px-4 rounded-md transition-colors disabled:opacity-60
                               text-sm"
                  >
                    {loading ? "Verifying…" : "Verify & Sign In"}
                  </button>
                  <button
                    data-testid="back-to-email-button"
                    type="button"
                    onClick={() => { setStep("email"); setOtp(""); setDevOtp(null); }}
                    className="w-full text-sm text-slate-500 hover:text-slate-700 transition-colors"
                  >
                    Back
                  </button>
                </form>
              </>
            )}
          </div>

          <p className="text-center text-xs text-slate-400 mt-6">
            Dynamo Gymnastics — Staff Portal
          </p>
        </div>
    </div>
  );
}
